import { useCallback, useMemo, useState } from "react";
import { useQuery } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { useMe } from "@/auth/use-me";
import { useChatStore, type ConversationMeta } from "@/stores/chat-store";
import { PersonsByIdsDocument } from "@/graphql/generated/operations";
import { Avatar, type PersonInfo } from "@/components/common/Avatar";

export function ConversationList() {
  const intl = useIntl();
  const me = useMe();
  const myId = useChatStore((s) => s.myId);
  const conversations = useChatStore((s) => s.conversations);
  const openChats = useChatStore((s) => s.openChats);
  const openChat = useChatStore((s) => s.openChat);
  const [search, setSearch] = useState("");

  const otherParticipantIds = useMemo(() => {
    const ids = new Set<string>();
    for (const convo of conversations.values()) {
      for (const pid of convo.participantIds) {
        if (pid !== myId) ids.add(pid);
      }
    }
    return [...ids];
  }, [conversations, myId]);

  const { data: personsData } = useQuery(PersonsByIdsDocument, {
    variables: { input: { first: 100, where: { id: { in: otherParticipantIds } } } },
    skip: otherParticipantIds.length === 0,
  });

  const personMap = useMemo(() => {
    const map = new Map<string, PersonInfo>();
    for (const p of personsData?.persons.nodes ?? []) {
      map.set(p.id, {
        name: p.formattedName ?? "Unknown",
        avatarContent: p.avatar?.content,
        avatarMime: p.avatar?.mimeType,
      });
    }
    return map;
  }, [personsData]);

  const otherFor = useCallback(
    (convo: { participantIds: string[] }): PersonInfo => {
      const otherId = convo.participantIds.find((id) => id !== myId);
      if (!otherId) {
        return {
          name: intl.formatMessage({ id: "chat.you", defaultMessage: "You" }),
          avatarContent: me.avatarContent ?? null,
          avatarMime: me.avatarMime ?? null,
        };
      }
      return (
        personMap.get(otherId) ?? {
          name: intl.formatMessage({ id: "common.loading", defaultMessage: "Loading…" }),
        }
      );
    },
    [myId, personMap, intl, me.avatarContent, me.avatarMime],
  );

  const convoList = useMemo(() => {
    const normalizedSearch = search.trim().toLocaleLowerCase(intl.locale);
    const activityMs = (convo: ConversationMeta) => {
      const marker = convo.unreadCount > 0
        ? (convo.unreadUpdatedAt ?? convo.lastMessage?.sentAt)
        : convo.lastMessage?.sentAt;
      const time = marker ? new Date(marker).getTime() : 0;
      return Number.isNaN(time) ? 0 : time;
    };

    return [...conversations.values()]
      .filter((convo) => {
        if (!normalizedSearch) return true;
        return otherFor(convo).name.toLocaleLowerCase(intl.locale).includes(normalizedSearch);
      })
      .sort((a, b) => {
        const byUnread = Number(b.unreadCount > 0) - Number(a.unreadCount > 0);
        if (byUnread !== 0) return byUnread;

        const byActivity = activityMs(b) - activityMs(a);
        if (byActivity !== 0) return byActivity;

        return a.id.localeCompare(b.id);
      });
  }, [conversations, intl.locale, otherFor, search]);

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="p-3">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={intl.formatMessage({
            id: "chat.searchChats",
            defaultMessage: "Search chats…",
          })}
          autoFocus
          className="w-full rounded ring-1 ring-ink/15 bg-surface px-3 py-1.5 text-sm outline-none focus:ring-accent"
        />
      </div>

      <div className="flex-1 overflow-y-auto">
        {conversations.size === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-ink/45">
              <FormattedMessage
                id="chat.noConversationsYet"
                defaultMessage="No conversations yet"
              />
            </p>
          </div>
        ) : convoList.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 text-center">
            <p className="text-sm text-ink/45">
              <FormattedMessage id="common.noResults" defaultMessage="No results found" />
            </p>
          </div>
        ) : (
          <ul>
            {convoList.map((convo) => (
              <ConversationRow
                key={convo.id}
                convo={convo}
                other={otherFor(convo)}
                isOpen={openChats.includes(convo.id)}
                onOpen={() => openChat(convo.id)}
              />
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

// One conversation in the list. Renders preview + unread purely from the
// store, which ChatDock's per-conversation bridge seeds and keeps live.
// Bold = unread.
function ConversationRow({
  convo,
  other,
  isOpen,
  onOpen,
}: {
  convo: ConversationMeta;
  other: PersonInfo;
  isOpen: boolean;
  onOpen: () => void;
}) {
  const intl = useIntl();
  const myId = useChatStore((s) => s.myId);
  const otherId = convo.participantIds.find((id) => id !== myId);
  const online =
    useChatStore((s) =>
      otherId ? s.presence.get(convo.id)?.get(otherId)?.online : undefined,
    ) ?? false;

  // Preview + unread render purely from the store: ChatDock's per-
  // conversation bridge is the single source that seeds and keeps both
  // live. The row used to run its own ConversationMessages + UnreadCount
  // queries, which duplicated the bridge's and re-hit the network on
  // every list open under cache-and-network.

  const unread = convo.unreadCount > 0;
  const last = convo.lastMessage;
  // Brand background tints instead of the neutral ink-overlay derivative.
  const rowState = unread ? "bg-page" : isOpen ? "bg-page/70" : "hover:bg-page/40";

  return (
    <li>
      <button
        type="button"
        onClick={onOpen}
        className={`relative flex w-full items-center gap-3 px-4 py-3 text-left ${rowState}`}
      >
        <Avatar
          person={other}
          online={online}
          onlineLabel={intl.formatMessage({ id: "chat.online", defaultMessage: "Online" })}
        />
        <div className="min-w-0 flex-1">
          <div className="flex items-baseline justify-between gap-2">
            <p className={`truncate text-sm ${unread ? "font-bold text-ink" : "font-medium"}`}>
              {other.name}
            </p>
            {last && (
              <span
                className={`shrink-0 text-[10px] tabular-nums ${
                  unread ? "font-medium text-accent" : "text-ink/40"
                }`}
              >
                {formatListTime(last.sentAt, intl.locale)}
              </span>
            )}
          </div>
          <div className="mt-0.5 flex items-center justify-between gap-2">
            <p className={`truncate text-xs ${unread ? "font-semibold text-ink" : "text-ink/45"}`}>
              {last?.content ?? " "}
            </p>
            <div className="flex shrink-0 items-center gap-1.5">
              {unread && (
                <span
                  className="flex h-4 min-w-4 items-center justify-center rounded-full bg-accent px-1 text-[10px] font-semibold text-on-accent"
                  aria-label={intl.formatMessage(
                    { id: "chat.unreadMessages", defaultMessage: "{count} unread" },
                    { count: convo.unreadCount },
                  )}
                  title={intl.formatMessage(
                    { id: "chat.unreadMessages", defaultMessage: "{count} unread" },
                    { count: convo.unreadCount },
                  )}
                >
                  {convo.unreadCount}
                </span>
              )}
            </div>
          </div>
        </div>
      </button>
    </li>
  );
}

// Compact timestamp for the list: time when today, day+month within the year,
// otherwise day+month+year.
function formatListTime(sentAt: string, locale: string): string {
  const d = new Date(sentAt);
  if (Number.isNaN(d.getTime())) return "";
  const now = new Date();
  const sameDay =
    d.getFullYear() === now.getFullYear() &&
    d.getMonth() === now.getMonth() &&
    d.getDate() === now.getDate();
  if (sameDay) {
    return d.toLocaleTimeString(locale, { hour: "2-digit", minute: "2-digit" });
  }
  if (d.getFullYear() === now.getFullYear()) {
    return d.toLocaleDateString(locale, { day: "numeric", month: "short" });
  }
  return d.toLocaleDateString(locale, { day: "numeric", month: "short", year: "numeric" });
}
